import { treaty } from "@elysiajs/eden";
import type { App } from "@groupeo/server";
import { env } from "@/src/common/constants/env";

const ACCESS_TOKEN_KEY = "auth.token";

let accessToken: string | null = typeof window !== "undefined" ? window.localStorage.getItem(ACCESS_TOKEN_KEY) : null;

export const getAccessToken = () => accessToken;

export const setAccessToken = (token: string | null) => {
	accessToken = token;
	if (typeof window === "undefined") return;
	if (token) window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
	else window.localStorage.removeItem(ACCESS_TOKEN_KEY);
};

export const httpClient = treaty<App>(env.SERVER_URL, {
	fetch: { credentials: "include" },
	headers: () => {
		const token = getAccessToken();
		return token ? { authorization: `Bearer ${token}` } : {};
	},
});

type EdenResponse<T> = { data: T | null; error: unknown };

const isUnauthorized = (error: unknown): boolean =>
	typeof error === "object" && error !== null && "status" in error && (error as { status: unknown }).status === 401;

let inFlightRefresh: Promise<boolean> | null = null;

const refreshAccessToken = (): Promise<boolean> => {
	if (inFlightRefresh) return inFlightRefresh;
	inFlightRefresh = (async () => {
		try {
			const res = await httpClient.auth.refresh.post({});
			if (res.error || !res.data) {
				setAccessToken(null);
				return false;
			}
			setAccessToken(res.data.access_token);
			return true;
		} catch {
			setAccessToken(null);
			return false;
		} finally {
			inFlightRefresh = null;
		}
	})();
	return inFlightRefresh;
};

export async function callWithRefresh<T>(call: () => Promise<EdenResponse<T>>): Promise<EdenResponse<T>> {
	const res = await call();
	if (!isUnauthorized(res.error)) return res;
	const refreshed = await refreshAccessToken();
	if (!refreshed) return res;
	return call();
}
